import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { CountUp, AnimatedPoly, AnimatedBar, AnimatedDonut } from "../components/Animate";
import {
  Briefcase, Award, GraduationCap, ShieldCheck, TrendingUp,
  Users, Building2, Sparkles, BarChart3, Target,
  Download, FileText, Clock, Trophy,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

/* ---------- export utilities ---------- */
/** Quote a value for CSV (handle commas, quotes, newlines). */
function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

/** Build a multi-section CSV string from { sectionName: [{col: val, ...}] }. */
function buildCsv(sections) {
  const lines = [];
  for (const [name, rows] of Object.entries(sections)) {
    lines.push(`# ${name}`);
    if (!rows || rows.length === 0) { lines.push("(no data)\n"); continue; }
    const cols = Object.keys(rows[0]);
    lines.push(cols.map(csvCell).join(","));
    for (const r of rows) lines.push(cols.map((c) => csvCell(r[c])).join(","));
    lines.push("");
  }
  return lines.join("\n");
}

/** Trigger a browser download for a string blob. */
function downloadBlob(text, filename, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

/** Convert role + analytics-data into a clean CSV with KPIs + every table-like section. */
function analyticsToCsv(role, data) {
  const sections = {};
  if (data.kpis) {
    sections.KPIs = Object.entries(data.kpis).map(([metric, value]) => ({ metric, value }));
  }
  if (data.funnel) {
    sections["Application Funnel"] = Object.entries(data.funnel).map(([stage, count]) => ({ stage, count }));
  }
  if (data.interviews_by_status) {
    sections["Interviews by Status"] = Object.entries(data.interviews_by_status).map(([status, count]) => ({ status, count }));
  }
  if (data.user_breakdown) {
    sections["Users by Role"] = Object.entries(data.user_breakdown).map(([role, count]) => ({ role, count }));
  }
  if (data.ai_engagement) {
    sections["AI & Engagement"] = Object.entries(data.ai_engagement).map(([metric, value]) => ({ metric, value }));
  }
  if (Array.isArray(data.employability_trend) && data.employability_trend.length) {
    sections["Employability Trend"] = data.employability_trend.map((r) => ({ date: r.date, score: r.count }));
  }
  if (data.career_ai?.students?.length) {
    sections["Cohort Career AI"] = data.career_ai.students.map((s) => ({
      student: s.name, course: s.course, employability: s.employability_score,
      ats: s.ats_score, target_role: s.target_role, readiness_pct: s.readiness_pct,
    }));
  }
  const tableKeys = [
    ["lms_progress",        "CoE Lab Progress"],
    ["top_companies_applied", "Top Companies Applied"],
    ["top_postings",        "Top Postings"],
    ["coe_demand",          "CoE Demand Mix"],
    ["top_skills",          "Top Skills"],
    ["coe_participation",   "CoE Participation"],
    ["top_recruiters",      "Top Recruiters"],
    ["top_labs",            "Top Labs"],
    ["apps_last_30d",       "Applications (Last 30 days)"],
    ["signups_last_30d",    "Signups (Last 30 days)"],
  ];
  for (const [k, name] of tableKeys) {
    if (Array.isArray(data[k]) && data[k].length) sections[name] = data[k];
  }
  return `SkillConnect ${role} analytics — exported ${new Date().toISOString()}\n\n${buildCsv(sections)}`;
}

/**
 * Capture an element to a PDF (multi-page if needed), with a SkillConnect
 * branded header + footer drawn on every page.
 *
 * Design notes:
 * • The body content is captured as a single tall canvas via html2canvas
 *   at high scale for crisp text, then sliced into per-page images so
 *   each page carries only the pixels that belong on it — no more offset
 *   trick, no more mask-hacks that used to bleed into the header.
 * • Header, footer, and inner gutters all use a single 4-pt vertical
 *   rhythm so every element sits on the same baseline grid.
 */
async function exportElementToPdf(el, filename, meta = {}) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);

  // Layout constants — everything measured in PDF points (1pt = 1/72 in).
  const HEADER_H  = 72;   // brand band
  const FOOTER_H  = 44;   // contact + page number band
  const MARGIN_X  = 32;   // side gutter
  const SAFE_GAP  = 12;   // breathing room between chrome and body
  const BRAND     = "#0040A8";
  const RULE      = "#E5E7EB";
  const TEXT_MUT  = "#6B7280";

  // Render at a higher scale so text stays crisp when scaled down to fit
  // the PDF body width.
  const canvas = await html2canvas(el, {
    backgroundColor: "#FFFFFF",
    scale: Math.min(window.devicePixelRatio * 2, 2.8),
    useCORS: true,
    logging: false,
    windowWidth: el.scrollWidth,
  });

  const pdf     = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const pageW   = pdf.internal.pageSize.getWidth();
  const pageH   = pdf.internal.pageSize.getHeight();
  const bodyW   = pageW - MARGIN_X * 2;
  const bodyTop = HEADER_H + SAFE_GAP;
  const bodyBot = pageH - FOOTER_H - SAFE_GAP;
  const bodyH   = bodyBot - bodyTop;

  // Scale factor mapping canvas-px → PDF-pt.
  const pxPerPt      = canvas.width / bodyW;
  const bodyHPx      = bodyH * pxPerPt;
  const totalPages   = Math.max(1, Math.ceil(canvas.height / bodyHPx));

  const ts       = new Date();
  const tsLabel  = ts.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const today    = ts.toISOString().slice(0, 10);

  const drawChrome = (pageNum) => {
    // ───── Header band ─────
    pdf.setFillColor(BRAND);
    pdf.rect(0, 0, pageW, HEADER_H, "F");

    // "SC" monogram on the left — vertically centered inside the band.
    const monoSize   = 40;
    const monoY      = (HEADER_H - monoSize) / 2;
    const monoCenter = monoY + monoSize / 2;
    pdf.setFillColor("#FFFFFF");
    pdf.roundedRect(MARGIN_X, monoY, monoSize, monoSize, 6, 6, "F");
    pdf.setTextColor(BRAND);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(17);
    pdf.text("SC", MARGIN_X + monoSize / 2, monoCenter, { align: "center", baseline: "middle" });

    // Wordmark + subtitle — vertically centered around the monogram center.
    const textX      = MARGIN_X + monoSize + 12;
    pdf.setTextColor("#FFFFFF");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text("SkillConnect", textX, monoCenter - 4, { baseline: "alphabetic" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    const subTitle = `${(meta.role || "Account").replace(/^\w/, c => c.toUpperCase())} Analytics  ·  ${tsLabel}`;
    pdf.setTextColor("#DCE7FF");
    pdf.text(subTitle, textX, monoCenter + 10, { baseline: "alphabetic" });

    // Right-side identity block — same vertical center as the monogram.
    if (meta.name || meta.email || meta.address) {
      pdf.setTextColor("#FFFFFF");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10.5);
      if (meta.name) {
        pdf.text(String(meta.name), pageW - MARGIN_X, monoCenter - 4, { align: "right", baseline: "alphabetic" });
      }
      pdf.setTextColor("#DCE7FF");
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.75);
      if (meta.email) {
        pdf.text(String(meta.email), pageW - MARGIN_X, monoCenter + 8, { align: "right", baseline: "alphabetic" });
      }
      if (meta.address) {
        pdf.text(String(meta.address).slice(0, 60), pageW - MARGIN_X, monoCenter + 20, { align: "right", baseline: "alphabetic" });
      }
    }

    // ───── Footer band ─────
    // Thin rule across the top of the footer zone, well clear of body text.
    const footTop = pageH - FOOTER_H;
    pdf.setDrawColor(RULE);
    pdf.setLineWidth(0.6);
    pdf.line(MARGIN_X, footTop, pageW - MARGIN_X, footTop);

    // Line 1 — contact strip (left) + generated date (right).
    pdf.setTextColor(TEXT_MUT);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text(
      "SkillConnect — Skills to Careers  ·  hello@skillconnect.com  ·  skillconnect.com",
      MARGIN_X, footTop + 16, { baseline: "alphabetic" },
    );
    pdf.text(`Generated ${today}`, pageW - MARGIN_X, footTop + 16, { align: "right", baseline: "alphabetic" });

    // Line 2 — page number, centered.
    pdf.setFontSize(7.5);
    pdf.text(`Page ${pageNum} of ${totalPages}`, pageW / 2, footTop + 30, {
      align: "center", baseline: "alphabetic",
    });
  };

  // Slice the tall canvas per-page. Each per-page canvas contains ONLY the
  // pixels that belong on that page — no offset trickery, no leakage into
  // header/footer.
  for (let i = 0; i < totalPages; i++) {
    if (i > 0) pdf.addPage();
    drawChrome(i + 1);

    const srcY = Math.round(i * bodyHPx);
    const srcH = Math.min(bodyHPx, canvas.height - srcY);
    if (srcH <= 0) continue;
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width  = canvas.width;
    sliceCanvas.height = srcH;
    const sctx = sliceCanvas.getContext("2d");
    // White fill so any transparent regions don't render dark on some viewers.
    sctx.fillStyle = "#FFFFFF";
    sctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
    const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.94);
    const sliceHpt  = srcH / pxPerPt;
    pdf.addImage(sliceData, "JPEG", MARGIN_X, bodyTop, bodyW, sliceHpt, undefined, "SLOW");
  }
  pdf.save(filename);
}

/* ---------- shared primitives ---------- */
function KPI({ label, value, suffix = "", icon: Icon, color = "var(--signal)", testId }) {
  return (
    <div className="card card-pad" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        {Icon ? <Icon size={16} style={{ color }} /> : null}
      </div>
      <div className="font-display font-black text-3xl mt-2" style={{ color }}>
        <CountUp value={Number(value) || 0} suffix={suffix} duration={1200} />
      </div>
    </div>
  );
}

function Section({ title, eyebrow, children, right }) {
  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
          <h3 className="font-display font-bold text-lg mt-1">{title}</h3>
        </div>
        {right || null}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-muted py-6 text-center">{children}</div>;
}

/** Sparkline — turns a `[{date, count}]` series into a polyline-line chart. */
function Sparkline({ data = [], color = "var(--signal)", height = 64 }) {
  if (!data.length) return <Empty>No activity in this window.</Empty>;
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 200;
  const h = height;
  const step = w / Math.max(1, data.length - 1);
  const pts = data
    .map((d, i) => `${(i * step).toFixed(1)},${(h - (d.count / max) * (h - 6) - 3).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}>
      <AnimatedPoly points={pts} color={color} delay={150} duration={1400} />
      {/* baseline */}
      <line x1="0" y1={h - 0.5} x2={w} y2={h - 0.5} stroke="var(--rule)" strokeWidth="0.5" />
    </svg>
  );
}

/** Horizontal bar list with smooth fill animation. */
function BarList({ rows = [], color = "var(--signal)", labelKey = "name", valueKey = "count" }) {
  if (!rows.length) return <Empty>Nothing to show yet.</Empty>;
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r[labelKey]}>
          <div className="flex items-center justify-between text-xs font-medium">
            <span className="truncate pr-2">{r[labelKey]}</span>
            <span className="font-mono font-bold tabular-nums">
              <CountUp value={Number(r[valueKey]) || 0} duration={900} />
            </span>
          </div>
          <div className="mt-0.5">
            <AnimatedBar pct={r[valueKey]} max={max} color={color} delay={80} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Funnel — converts {pending, interview, offer, rejected, …} into staged bars. */
function Funnel({ data = {}, stages, accent = "var(--signal)" }) {
  const items = stages.map(([k, label, color]) => ({
    key: k, label, color: color || accent, count: Number(data[k]) || 0,
  }));
  const total = Math.max(1, items.reduce((s, x) => s + x.count, 0));
  return (
    <div className="space-y-2">
      {items.map((it) => {
        const pct = Math.round((it.count / total) * 100);
        return (
          <div key={it.key}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold">{it.label}</span>
              <span className="font-mono tabular-nums text-muted">
                <CountUp value={it.count} duration={900} /> ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-rule rounded-sm mt-1 overflow-hidden">
              <div
                className="h-full transition-[width] duration-1000 ease-out"
                style={{ width: `${pct}%`, background: it.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- role views ---------- */

function StudentView({ data }) {
  const k = data.kpis || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="student-kpis">
        <KPI label="Applications" value={k.applications_total} icon={Briefcase} testId="kpi-apps" />
        <KPI label="Interviews" value={k.interviews_total} icon={Sparkles} color="#9933FF" testId="kpi-iv" />
        <KPI label="Certificates" value={k.certificates} icon={Award} color="#0099FF" testId="kpi-certs" />
        <KPI label="Labs Active" value={k.labs_enrolled} icon={GraduationCap} testId="kpi-labs" />
        <KPI label="Labs Completed" value={k.labs_completed} icon={ShieldCheck} testId="kpi-labs-done" />
        <KPI label="CoE Badges" value={k.badges} icon={ShieldCheck} color="#0040A8" testId="kpi-badges" />
        <KPI label="Profile %" value={k.profile_completeness} suffix="%" icon={TrendingUp} testId="kpi-profile" />
        <KPI label="30-day Apps" value={k.applications_last_30d} icon={BarChart3} color="#0099FF" testId="kpi-30d" />
        <KPI label="Employability" value={k.employability_score ?? 0} suffix="/100" icon={Target} color="#16A34A" testId="kpi-employability" />
        <KPI label="Mock Interviews" value={k.mock_interviews} icon={Sparkles} color="#EA580C" testId="kpi-mocks" />
        <KPI label="Best Mock Score" value={k.best_mock_score ?? 0} icon={Trophy} color="#9933FF" testId="kpi-mock-best" />
        <KPI label="XP Points" value={k.xp_points} icon={TrendingUp} color="#0040A8" testId="kpi-xp" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section eyebrow="Career AI" title="Employability trend">
          {(data.employability_trend || []).length > 1 ? (
            <Sparkline data={data.employability_trend} color="#16A34A" />
          ) : (
            <Empty>
              Your score history builds as you hit milestones.{" "}
              <Link to="/student/career" className="text-signal font-semibold">Open Career AI</Link>.
            </Empty>
          )}
        </Section>
        <Section eyebrow="Pipeline" title="Application funnel">
          <Funnel
            data={data.funnel || {}}
            stages={[
              ["pending", "Pending", "#0099FF"],
              ["interview", "Interview", "#0040A8"],
              ["offer", "Offer", "#16A34A"],
              ["rejected", "Rejected", "#DC2626"],
              ["withdrawn", "Withdrawn", "#94A3B8"],
            ]}
          />
        </Section>

        <Section eyebrow="Last 30 days" title="Applications submitted">
          <Sparkline data={data.apps_last_30d || []} color="var(--signal)" />
        </Section>

        <Section eyebrow="CoE labs" title="Lab progress">
          <div className="space-y-3" data-testid="student-lms-progress">
            {(data.lms_progress || []).length === 0 ? (
              <Empty>
                Not enrolled in any lab.{" "}
                <Link to="/student/lms" className="text-signal font-semibold">Browse labs</Link>.
              </Empty>
            ) : (
              (data.lms_progress || []).map((l) => (
                <div key={l.lab_id}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono font-bold" style={{ color: l.lab_color }}>{l.lab_code}</span>
                    <span className="font-mono tabular-nums">{l.progress_pct}%</span>
                  </div>
                  <div className="h-2 bg-rule rounded-sm mt-1 overflow-hidden">
                    <div className="h-full transition-[width] duration-1000"
                      style={{ width: `${l.progress_pct}%`, background: l.lab_color }} />
                  </div>
                  <div className="text-[11px] text-muted mt-0.5 truncate">{l.lab_name}</div>
                </div>
              ))
            )}
          </div>
        </Section>
      </div>

      <Section eyebrow="Top targets" title="Companies you applied to">
        <BarList rows={data.top_companies_applied || []} color="#0040A8" />
      </Section>
    </div>
  );
}

function CompanyView({ data }) {
  const k = data.kpis || {};
  const totalHired = k.hires_total || 0;
  const totalOffer = k.offers_total || 0;
  const acceptRate = totalOffer ? Math.round((totalHired / totalOffer) * 100) : 0;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="company-kpis">
        <KPI label="Open Positions" value={k.postings_active} icon={Briefcase} color="#0040A8" testId="kpi-posts-active" />
        <KPI label="Applications" value={k.applications_total} icon={Users} color="#006CFF" testId="kpi-apps" />
        <KPI label="Interviews" value={k.interviews_total} icon={Sparkles} color="#9933FF" testId="kpi-iv" />
        <KPI label="Offers" value={k.offers_total} icon={Target} color="#0099FF" testId="kpi-offers" />
        <KPI label="Hires" value={k.hires_total} icon={Award} color="#16A34A" testId="kpi-hires" />
        <KPI label="Time to Hire" value={k.time_to_hire_days || "—"} suffix=" d" icon={Clock} color="#EA580C" testId="kpi-tth" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section eyebrow="Pipeline" title="Recruitment funnel">
          <Funnel
            data={data.funnel || {}}
            stages={[
              ["pending",   "Applications",  "#0099FF"],
              ["screening", "Shortlisted",   "#0040A8"],
              ["interview", "Interviewed",   "#9933FF"],
              ["offer",     "Offered",       "#EA580C"],
              ["hired",     "Hired",         "#16A34A"],
              ["rejected",  "Rejected",      "#DC2626"],
            ]}
          />
        </Section>

        <Section eyebrow="Last 30 days" title="Hiring trend (Applications · Hires)">
          <DualSparkline
            a={data.apps_last_30d || []}
            b={data.hires_last_30d || []}
            colorA="#0040A8"
            colorB="#16A34A"
            labelA="Applications"
            labelB="Hires"
          />
        </Section>

        <Section eyebrow="Channels" title="Source performance">
          <SourceDonut rows={data.source_mix || []} />
        </Section>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section eyebrow="By department" title="Department breakdown" right={<span className="text-[11px] text-muted">Applications</span>}>
          <BarList rows={data.department_breakdown || []} color="#0099FF" />
        </Section>

        <Section eyebrow="Demand mix" title="CoE skill demand">
          <BarList rows={data.coe_demand || []} color="#9933FF" labelKey="code" />
        </Section>

        <Section eyebrow="Acceptance" title="Offer → Hire conversion">
          <div className="flex flex-col items-center text-center mt-3">
            <div className="font-display font-black text-5xl" style={{ color: acceptRate >= 60 ? "#16A34A" : acceptRate >= 30 ? "#0040A8" : "#DC2626" }}>
              <CountUp value={acceptRate} suffix="%" duration={1000} />
            </div>
            <div className="text-xs text-muted mt-1">{totalHired} hired from {totalOffer} offers</div>
            <div className="text-[11px] text-muted mt-3">
              Industry benchmark: <b>60-75%</b>. Below 50% suggests offers need a stronger close.
            </div>
          </div>
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Section eyebrow="Top performers" title="Postings by applications">
          {(data.top_postings || []).length === 0 ? (
            <Empty>No postings yet — <Link to="/company/new" className="text-signal font-semibold">create your first posting</Link>.</Empty>
          ) : (
            <div className="space-y-2.5">
              {(data.top_postings || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-rule last:border-0"
                  data-testid={`top-post-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    <div className="text-[11px] text-muted">{p.type} · {p.status} · {p.created_at}</div>
                  </div>
                  <span className="font-mono font-bold text-sm tabular-nums">
                    <CountUp value={p.applications} duration={700} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section eyebrow="Team" title="Recruiter performance" right={<Trophy size={14} className="text-muted" />}>
          <RecruiterTable rows={data.recruiter_performance || []} />
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Section eyebrow="Posting requirements" title="Most-requested skills">
          <BarList rows={data.top_skills || []} color="var(--signal)" labelKey="skill" />
        </Section>

        <Section eyebrow="Coverage" title="Postings status">
          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat label="Active" value={k.postings_active} color="#16A34A" />
            <MiniStat label="Closed" value={k.postings_closed} color="#94A3B8" />
            <MiniStat label="Total" value={k.postings_total} color="#0040A8" />
          </div>
          <div className="mt-4 text-[12px] text-muted">
            Unique applicants reached: <b>{k.applicants_unique || 0}</b> · 30-day apps: <b>{k.applications_last_30d || 0}</b>
          </div>
        </Section>
      </div>
    </div>
  );
}

/* ---------- helpers used inside CompanyView ---------- */
function MiniStat({ label, value, color }) {
  return (
    <div>
      <div className="font-display font-black text-2xl" style={{ color }}>
        <CountUp value={Number(value) || 0} duration={700} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function DualSparkline({ a = [], b = [], colorA, colorB, labelA, labelB, height = 90 }) {
  if (!a.length && !b.length) return <Empty>No activity yet.</Empty>;
  const max = Math.max(1, ...a.map((d) => d.count), ...b.map((d) => d.count));
  const w = 220;
  const stepA = w / Math.max(1, a.length - 1);
  const stepB = w / Math.max(1, b.length - 1);
  const ptsA = a.map((d, i) => `${(i * stepA).toFixed(1)},${(height - (d.count / max) * (height - 6) - 3).toFixed(1)}`).join(" ");
  const ptsB = b.map((d, i) => `${(i * stepB).toFixed(1)},${(height - (d.count / max) * (height - 6) - 3).toFixed(1)}`).join(" ");
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
        <AnimatedPoly points={ptsA} color={colorA} delay={120} duration={1400} />
        <AnimatedPoly points={ptsB} color={colorB} delay={240} duration={1400} />
        <line x1="0" y1={height - 0.5} x2={w} y2={height - 0.5} stroke="var(--rule)" strokeWidth="0.5" />
      </svg>
      <div className="flex items-center justify-center gap-4 text-[11px] mt-2">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: colorA }} /> {labelA}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: colorB }} /> {labelB}</span>
      </div>
    </div>
  );
}

function SourceDonut({ rows = [] }) {
  if (!rows.length) return <Empty>No source data yet.</Empty>;
  const palette = ["#006CFF", "#9933FF", "#0099FF", "#16A34A", "#EA580C", "#0040A8", "#DC2626", "#94A3B8"];
  const segments = rows.map((r, i) => ({ value: r.count, color: palette[i % palette.length] }));
  const total = rows.reduce((s, r) => s + (r.count || 0), 0) || 1;
  return (
    <div className="flex items-center gap-4">
      <AnimatedDonut size={110} stroke={18} segments={segments} />
      <div className="flex-1 space-y-1.5">
        {rows.map((r, i) => (
          <div key={r.source} className="flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: palette[i % palette.length] }} />
              <span className="truncate">{r.source}</span>
            </span>
            <span className="font-mono tabular-nums font-bold">{r.count} <span className="text-muted font-normal">({Math.round((r.count / total) * 100)}%)</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecruiterTable({ rows = [] }) {
  if (!rows.length) return <Empty>No recruiter activity yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="dense" data-testid="recruiter-perf-table">
        <thead><tr><th>Recruiter</th><th className="text-right">Offers</th><th className="text-right">Hires</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="font-medium">{r.name}</td>
              <td className="text-right font-mono"><CountUp value={r.offers} duration={700} /></td>
              <td className="text-right font-mono font-bold" style={{ color: "#16A34A" }}><CountUp value={r.hires} duration={700} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InstituteView({ data }) {
  const k = data.kpis || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="institute-kpis">
        <KPI label="Students" value={k.students_total} icon={GraduationCap} testId="kpi-stu" />
        <KPI label="Placed" value={k.students_placed} icon={Award} color="#16A34A" testId="kpi-placed" />
        <KPI label="Placement %" value={k.placement_pct} suffix="%" icon={TrendingUp} color="#9933FF" testId="kpi-pct" />
        <KPI label="With Apps" value={k.students_with_applications} icon={Briefcase} color="#0040A8" testId="kpi-apps" />
        <KPI label="Avg Profile %" value={k.avg_profile_completeness} suffix="%" icon={Target} testId="kpi-profile" />
        <KPI label="LMS Enrolled" value={k.lms_enrollments} icon={GraduationCap} color="#0099FF" testId="kpi-enrol" />
        <KPI label="LMS Completed" value={k.lms_completions} icon={ShieldCheck} testId="kpi-done" />
        <KPI label="Joined 30d" value={k.students_active_30d} icon={Users} color="#0040A8" testId="kpi-new" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section eyebrow="LMS Mix" title="CoE participation across batch">
          <BarList rows={data.coe_participation || []} color="#9933FF" labelKey="code" />
        </Section>
        <Section eyebrow="Industry" title="Top recruiting companies">
          <BarList rows={data.top_recruiters || []} color="#0040A8" labelKey="name" />
        </Section>
      </div>

      <CohortCareerAI ca={data.career_ai || {}} />

      {k.students_total === 0 && (
        <div className="card card-pad text-sm text-muted" data-testid="institute-empty-hint">
          We couldn&apos;t match students to your institute name yet — invite students from{" "}
          <Link to="/institution/invites" className="text-signal font-semibold">Invitations</Link>{" "}
          and analytics will populate as their profiles include your institute.
        </div>
      )}
    </div>
  );
}

function CohortCareerAI({ ca }) {
  const [nudge, setNudge] = useState({ busy: false, msg: "", err: false });
  const sendNudge = async () => {
    setNudge({ busy: true, msg: "", err: false });
    try {
      const r = await api.post("/analytics/institute/nudge");
      const { nudged, skipped_recent } = r.data;
      setNudge({
        busy: false, err: false,
        msg: nudged > 0
          ? `Nudged ${nudged} student${nudged === 1 ? "" : "s"}${skipped_recent ? ` (${skipped_recent} already nudged in the last 24h)` : ""}.`
          : skipped_recent > 0
            ? `All ${skipped_recent} students below target were already nudged in the last 24h.`
            : "Everyone is already above target — nothing to nudge!",
      });
    } catch (e) {
      setNudge({ busy: false, err: true, msg: "Could not send nudges — please retry." });
    }
  };
  const dist = ca.distribution || [];
  const distMax = Math.max(1, ...dist.map(d => d.count));
  const BUCKET_COLORS = { "0-39": "#DC2626", "40-59": "#EA580C", "60-79": "#006CFF", "80-100": "#16A34A" };
  return (
    <div className="space-y-6" data-testid="cohort-career-ai">
      <Section eyebrow="Career AI · Data lineage" title="Cohort employability & skill gaps">
        <div className="flex items-center justify-between flex-wrap gap-2 -mt-1 mb-4">
          <p className="text-xs text-muted">
            Students scoring below 60 (or without an AI analysis) can be nudged to get job-ready.
          </p>
          <button className="btn-secondary" onClick={sendNudge} disabled={nudge.busy}
                  data-testid="nudge-students-btn">
            {nudge.busy ? "Sending…" : "Nudge students"}
          </button>
        </div>
        {nudge.msg && (
          <div className={`text-xs mb-4 font-semibold ${nudge.err ? "text-red-600" : "text-green-700"}`}
               data-testid="nudge-result">
            {nudge.msg}
          </div>
        )}
        {ca.scored === 0 ? (
          <Empty>
            No students have an employability score yet. Scores appear automatically as your
            students complete profiles, upload resumes and run their AI analyses.
          </Empty>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="grid grid-cols-2 gap-4">
              {[["Avg Employability", `${ca.avg_employability}/100`, "#16A34A"],
                ["Students Scored", ca.scored, "#006CFF"],
                ["Profile Analyses", ca.profile_analyses, "#9933FF"],
                ["Skill Gap Analyses", ca.skill_gap_analyses, "#0099FF"]].map(([label, value, color]) => (
                <div key={label} className="text-center border border-rule rounded-xl py-3 px-2">
                  <div className="font-display font-black text-2xl" style={{ color }}>{value}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{label}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold text-muted mb-2">Employability distribution</div>
              <div className="space-y-2">
                {dist.map(d => (
                  <div key={d.bucket} data-testid={`emp-dist-${d.bucket}`}>
                    <div className="flex justify-between text-xs">
                      <span className="font-mono text-muted">{d.bucket}</span>
                      <span className="font-semibold">{d.count}</span>
                    </div>
                    <div className="h-2 bg-rule rounded-full mt-0.5 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                           style={{ width: `${(d.count / distMax) * 100}%`, background: BUCKET_COLORS[d.bucket] }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Section>

      {(ca.top_missing_skills || []).length + (ca.top_target_roles || []).length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section eyebrow="Skill gaps" title="Most-missing skills in your cohort">
            <BarList rows={ca.top_missing_skills || []} color="#DC2626" labelKey="name" />
          </Section>
          <Section eyebrow="Aspirations" title="Top target roles">
            <BarList rows={ca.top_target_roles || []} color="#9933FF" labelKey="name" />
          </Section>
        </div>
      )}

      {(ca.students || []).length > 0 && (
        <Section eyebrow="Student-level" title="Employability leaderboard">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cohort-student-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-rule">
                  <th className="py-2 pr-3">Student</th>
                  <th className="py-2 pr-3">Course</th>
                  <th className="py-2 pr-3 text-right">Employability</th>
                  <th className="py-2 pr-3 text-right">ATS</th>
                  <th className="py-2 pr-3">Target role</th>
                  <th className="py-2 text-right">Readiness</th>
                </tr>
              </thead>
              <tbody>
                {ca.students.map((s, i) => (
                  <tr key={`${s.name}-${i}`} className="border-b border-rule/60 last:border-0">
                    <td className="py-2 pr-3 font-semibold">{s.name}</td>
                    <td className="py-2 pr-3 text-muted">{s.course || "—"}</td>
                    <td className="py-2 pr-3 text-right">
                      {s.employability_score != null
                        ? <span className="font-mono font-bold" style={{ color: s.employability_score >= 70 ? "#16A34A" : s.employability_score >= 40 ? "#006CFF" : "#EA580C" }}>{s.employability_score}</span>
                        : <span className="text-muted text-xs">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-muted">{s.ats_score ?? "—"}</td>
                    <td className="py-2 pr-3 text-muted">{s.target_role || "—"}</td>
                    <td className="py-2 text-right font-mono text-muted">{s.readiness_pct != null ? `${s.readiness_pct}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function AiEngagementGrid({ ai }) {
  const stats = [
    ["Avg Employability", `${ai.employability_avg ?? 0}/100`, "#16A34A"],
    ["Students Scored", ai.employability_scored, "#006CFF"],
    ["Profile Analyses", ai.profile_analyses, "#9933FF"],
    ["Skill Gap Analyses", ai.skill_gap_analyses, "#0099FF"],
    ["Mock Interviews", `${ai.mock_interviews_completed ?? 0}/${ai.mock_interviews_total ?? 0}`, "#EA580C"],
    ["Avg Mock Score", ai.mock_interview_avg_score, "#0040A8"],
    ["Study Plans", ai.study_plans_generated, "#16A34A"],
    ["Sprint Answers", `${ai.sprint_correct ?? 0}/${ai.sprint_answers ?? 0}`, "#9933FF"],
    ["Sprint Players", ai.sprint_players, "#006CFF"],
    ["Conquest Submissions", ai.conquest_submissions, "#EA580C"],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="ai-engagement-grid">
      {stats.map(([label, value, color]) => (
        <div key={label} className="text-center border border-rule rounded-xl py-3 px-2">
          <div className="font-display font-black text-xl" style={{ color }}>{value ?? 0}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
}

function AdminView({ data }) {
  const k = data.kpis || {};
  const ub = data.user_breakdown || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-kpis">
        <KPI label="Total Users" value={k.users_total} icon={Users} testId="kpi-users" />
        <KPI label="Students" value={k.students_total} icon={GraduationCap} color="#0040A8" testId="kpi-stu" />
        <KPI label="Working Pros" value={k.working_pros_total} icon={Users} color="#16A34A" testId="kpi-pros" />
        <KPI label="Companies" value={k.companies_total} icon={Briefcase} color="#9933FF" testId="kpi-co" />
        <KPI label="Institutes" value={k.institutes_total} icon={Building2} color="#0099FF" testId="kpi-inst" />
        <KPI label="Postings (active)" value={k.postings_active} icon={Briefcase} color="#16A34A" testId="kpi-posts" />
        <KPI label="Applications" value={k.applications_total} icon={Target} testId="kpi-apps" />
        <KPI label="Pending Students" value={k.pending_students} icon={Sparkles} color="#EA580C" testId="kpi-pend" />
        <KPI label="Approved Students" value={k.approved_students} icon={ShieldCheck} color="#16A34A" testId="kpi-appr" />
        <KPI label="LMS Enrolments" value={k.lms_enrollments} icon={GraduationCap} color="#0099FF" testId="kpi-enrol" />
        <KPI label="LMS Completions" value={k.lms_completions} icon={Award} testId="kpi-done" />
        <KPI label="Certificates" value={k.certificates} icon={ShieldCheck} color="#9933FF" testId="kpi-certs" />
        <KPI label="Total Postings" value={k.postings_total} icon={BarChart3} color="#0040A8" testId="kpi-postsall" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Section eyebrow="Last 30 days" title="New signups">
          <Sparkline data={data.signups_last_30d || []} color="#0040A8" />
        </Section>
        <Section eyebrow="Last 30 days" title="New applications">
          <Sparkline data={data.apps_last_30d || []} color="var(--signal)" />
        </Section>
        <Section eyebrow="Role mix" title="Users by role">
          <div className="flex items-center justify-center">
            <AnimatedDonut
              size={120}
              stroke={18}
              segments={[
                { value: ub.students || 0, color: "#006CFF" },
                { value: ub.working_pros || 0, color: "#16A34A" },
                { value: ub.companies || 0, color: "#9933FF" },
                { value: ub.institutes || 0, color: "#0099FF" },
                { value: ub.admin || 0, color: "#94A3B8" },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs mt-4">
            <span><span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: "#006CFF" }} /> Students <b>{ub.students || 0}</b></span>
            <span><span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: "#16A34A" }} /> Working Pros <b>{ub.working_pros || 0}</b></span>
            <span><span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: "#9933FF" }} /> Companies <b>{ub.companies || 0}</b></span>
            <span><span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: "#0099FF" }} /> Institutes <b>{ub.institutes || 0}</b></span>
            <span><span className="inline-block w-2 h-2 mr-1.5 align-middle" style={{ background: "#94A3B8" }} /> Admin <b>{ub.admin || 0}</b></span>
          </div>
        </Section>
      </div>

      <Section eyebrow="Data lineage" title="AI & engagement across the platform">
        <AiEngagementGrid ai={data.ai_engagement || {}} />
      </Section>

      <div className="grid md:grid-cols-2 gap-6">
        <Section eyebrow="LMS" title="Top labs by enrolment">
          <BarList rows={data.top_labs || []} color="#9933FF" labelKey="name" />
        </Section>
        <Section eyebrow="Industry" title="Top recruiters">
          <BarList rows={data.top_recruiters || []} color="#0040A8" labelKey="name" />
        </Section>
      </div>
    </div>
  );
}

/* ---------- entry ---------- */
export default function Analytics() {
  const { user } = useAuth();
  const role = user?.role;
  usePageTitle(role === "company" ? "Hiring Analytics" : role === "institution" ? "Placement Analytics" : role === "admin" ? "Platform Analytics" : "Analytics");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (!role) return undefined;
    const endpoint = role === "working_pro" ? "student" : role === "institution" ? "institute" : role;
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api.get(`/analytics/${endpoint}`, { signal: ac.signal });
        if (!ac.signal.aborted) {
          setData(r.data);
          setErr("");
        }
      } catch (e) {
        if (!ac.signal.aborted) setErr(e?.response?.data?.detail || "Could not load analytics.");
      }
    })();
    return () => ac.abort();
  }, [role]);

  const onCsv = () => {
    if (!data) return;
    const ts = new Date().toISOString().slice(0, 10);
    downloadBlob(analyticsToCsv(role, data), `skillconnect-${role}-analytics-${ts}.csv`);
  };
  const onPdf = async () => {
    if (!data || pdfBusy) return;
    const el = document.querySelector(`[data-testid="analytics-${role}"]`);
    if (!el) return;
    setPdfBusy(true);
    try {
      const ts = new Date().toISOString().slice(0, 10);
      const meta = {
        role,
        name:  user?.name  || user?.organization || "",
        email: user?.email || "",
        address: [user?.city, user?.state].filter(Boolean).join(", "),
      };
      await exportElementToPdf(el, `skillconnect-${role}-analytics-${ts}.pdf`, meta);
    } catch (e) {
      setErr("PDF export failed — try downloading the CSV.");
    } finally {
      setPdfBusy(false);
    }
  };

  if (!role) {
    return <div className="card card-pad text-muted">Please log in to see analytics.</div>;
  }
  if (err && !data) {
    return <div className="card card-pad text-red-600" data-testid="analytics-error">{err}</div>;
  }
  if (!data) {
    return <div className="card card-pad text-muted animate-pulse" data-testid="analytics-loading">Loading analytics…</div>;
  }

  const heading = {
    student: "Your career analytics",
    working_pro: "Your career analytics",
    company: "Hiring analytics",
    institution: "Placement analytics",
    admin: "Platform analytics",
  }[role] || "Analytics";

  return (
    <div className="space-y-6" data-testid={`analytics-${role}`}>
      <div className="rounded-sm p-6 text-white bg-brand-gradient flex items-start justify-between gap-4 flex-wrap" data-testid="analytics-hero">
        <div>
          <span className="eyebrow on-grad-muted">Analytics</span>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1 text-white">{heading}</h2>
          <p className="on-grad-muted text-sm mt-2 max-w-xl">
            Real-time view across your activity on SkillConnect. Numbers update as people apply, learn and get hired.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden" data-testid="analytics-export-toolbar">
          <button
            type="button"
            onClick={onCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-semibold uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5 border border-white/20"
            title="Download all analytics as CSV"
          >
            <FileText size={14} /> CSV
          </button>
          <button
            type="button"
            onClick={onPdf}
            disabled={pdfBusy}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white text-signal text-xs font-bold uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60 disabled:translate-y-0"
            title="Download a PDF snapshot of this dashboard"
          >
            <Download size={14} /> {pdfBusy ? "Generating…" : "PDF"}
          </button>
        </div>
      </div>

      {(role === "student" || role === "working_pro") && <StudentView data={data} />}
      {role === "company"   && <CompanyView   data={data} />}
      {role === "institution" && <InstituteView data={data} />}
      {role === "admin"     && <AdminView     data={data} />}
    </div>
  );
}
